import React, { useCallback, useState } from "react";
import { entities } from "byzantine";
import CheckDetailContext from "./context";
import type { CheckDetailProviderProps } from "./types";

const CheckDetailProvider = ({
  payFrom,
  payTo,
  amount,
  memo,
  checkDate,
  checkNumber,
  checkDetailUuid,
  transactionId,
  onSaveSuccess,
  onDescriptionUpdate,
  children,
}: CheckDetailProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [displayPayFrom, setDisplayPayFrom] = useState(payFrom);
  const [displayPayTo, setDisplayPayTo] = useState(payTo);
  const [displayMemo, setDisplayMemo] = useState(memo);
  const [displayAmount, setDisplayAmount] = useState(amount);
  const [displayCheckDate, setDisplayCheckDate] = useState(checkDate);
  const [displayCheckNumber, setDisplayCheckNumber] = useState(checkNumber);
  const [editPayFrom, setEditPayFrom] = useState(payFrom || "");
  const [editPayTo, setEditPayTo] = useState(payTo || "");
  const [editMemo, setEditMemo] = useState(memo || "");
  const [editAmount, setEditAmount] = useState(amount || "");
  const [editCheckDate, setEditCheckDate] = useState(checkDate || "");
  const [editCheckNumber, setEditCheckNumber] = useState(checkNumber || "");

  const { send: patchCheckDetail, loading: isSaving } =
    entities.transactions.usePatchCheckDetail();

  const toggleCollapsed = useCallback(
    () => setIsCollapsed((prev) => !prev),
    []
  );

  const handleEdit = useCallback(() => {
    setEditPayFrom(displayPayFrom || "");
    setEditPayTo(displayPayTo || "");
    setEditMemo(displayMemo || "");
    setEditAmount(displayAmount || "");
    setEditCheckDate(displayCheckDate || "");
    setEditCheckNumber(displayCheckNumber || "");
    setIsEditing(true);
  }, [
    displayPayFrom,
    displayPayTo,
    displayAmount,
    displayMemo,
    displayCheckDate,
    displayCheckNumber,
  ]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    const strippedAmount = editAmount.replace(/[$,]/g, "");
    await patchCheckDetail(
      transactionId,
      checkDetailUuid || "",
      {
        pay_from: editPayFrom || undefined,
        pay_to: editPayTo || undefined,
        amount: strippedAmount || undefined,
        memo: editMemo || undefined,
        check_date: editCheckDate || null,
        check_number: editCheckNumber || undefined,
      },
      {
        onSuccess: (data) => {
          setDisplayPayFrom(editPayFrom || null);
          setDisplayPayTo(editPayTo || null);
          setDisplayAmount(strippedAmount || null);
          setDisplayMemo(editMemo || null);
          setDisplayCheckDate(editCheckDate || null);
          setDisplayCheckNumber(editCheckNumber || null);
          setIsEditing(false);
          if (data?.description) {
            onDescriptionUpdate?.(data.description);
          }
          onSaveSuccess?.();
        },
      }
    );
  }, [
    patchCheckDetail,
    transactionId,
    checkDetailUuid,
    editPayFrom,
    editPayTo,
    editAmount,
    editMemo,
    editCheckDate,
    editCheckNumber,
    onSaveSuccess,
    onDescriptionUpdate,
  ]);

  return (
    <CheckDetailContext.Provider
      value={{
        isCollapsed,
        isEditing,
        checkDetailUuid,
        displayPayFrom,
        displayPayTo,
        displayMemo,
        displayAmount,
        displayCheckDate,
        displayCheckNumber,
        editPayFrom,
        editPayTo,
        editMemo,
        editAmount,
        editCheckDate,
        editCheckNumber,
        setEditPayFrom,
        setEditPayTo,
        setEditMemo,
        setEditAmount,
        setEditCheckDate,
        setEditCheckNumber,
        toggleCollapsed,
        handleEdit,
        handleCancel,
        handleSave,
        isSaving,
      }}
    >
      {children}
    </CheckDetailContext.Provider>
  );
};

export default CheckDetailProvider;
